import { NativeApi, NativeBridge, NativeGeolocationApi } from '@portal/react-native-api';
import { Remote, createEndpoint } from 'comlink';
import { inject, injectable } from 'inversify';

import { Actions } from '../Actions';
import { AppBehavior } from './AppBehavior';
import { ContainerTypes } from './types/ContainerTypes';
import { EventEmitter } from 'events';
import { GeocoderEntry } from '../interfaces/search/GeocoderEntry';
import { RequestGeocoder } from './RequestGeocoder';
import { SearchTypes } from './types/SearchTypes';
import { copyFileSync } from 'node:fs';
import { formatAddressLine } from './util';
import ym from 'js.api';

type GeoCodeResult = {
	center: ym.provider.LatLngLiteral;
	addressLine: string;
	geocoded: GeocoderEntry[];
};

@injectable()
export class GPS {
	@inject(SearchTypes.Geocoding)
	geocoder!: RequestGeocoder;

	@inject(ContainerTypes.Action)
	actions!: Actions;

	@inject('behavior')
	appBehavior!: AppBehavior;

	center!: ym.provider.LatLngLiteral | void;
	value = '';
	hasValue = false;

	readonly events = new EventEmitter();

	private factory = new NativeBridge();
	private api = this.factory.getNativeApi() as Remote<NativeApi<NativeGeolocationApi>>;

	localize(options: PositionOptions = {}): void {
		console.debug('localize : ', { with: this.appBehavior.appView ? 'native' : 'browser' });

		const nativeLocationRequest = () =>
			this.api
				.requestLocation()
				.then((position) => this.geocode({ lat: position.coords.latitude, lng: position.coords.longitude }))
				.catch(() => {
					// react native api is not loaded when using ?isApp=true in browser (for debugging)
					console.debug('using browser navigator to localize user');
					return this.geolocate();
				});
		const browserLocationRequest = () => this.geolocate(options);

		const locationRequest = this.appBehavior.appView ? nativeLocationRequest() : browserLocationRequest();

		locationRequest
			.then((position) => this.geocode(position as ym.provider.LatLngLiteral))
			.then(({center, addressLine, geocoded}: GeoCodeResult) =>
				this.events.emit('localized', { center, addressLine, geocoded })
			)
			.catch((err: unknown) => this.events.emit('error', err));
	}

	private async geocode(center: ym.provider.LatLngLiteral): Promise<GeoCodeResult> {
		const geocoded = await this.geocoder.geocode({
			type: 'Geocoding',
			position: center,
			isoCountry: 'DE'
		});

		const hasEntries = geocoded.length > 0;
		const addressLine = hasEntries ? formatAddressLine(geocoded[0].address) : '';

		return { center, addressLine, geocoded };
	}

	private geolocate(options: PositionOptions = {}): Promise<ym.provider.LatLngLiteral> {
		return new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				position => {
					this.center = {
						lat: parseFloat(position.coords.latitude.toFixed(5)),
						lng: parseFloat(position.coords.longitude.toFixed(5))
					};
					resolve(this.center);
				},
				(error: GeolocationPositionError) => {
					reject(error);
				},
				{
					enableHighAccuracy: true,
					timeout: 20000,
					...options
				}
			);
		});
	}
}
